import React, { useEffect, useState } from 'react';
import {
  Badge, Button, Group, MultiSelect, NumberInput, Paper, Select, Stack,
  Text, Textarea, Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { TimeInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { IconHelp } from '@tabler/icons-react';
import HttpClient from '../helpers/HttpClient';
import { TrainingEvent, TrainingEventType } from '../types/training-event.type';
import { Tag } from '../types/tag.type';
import { Person } from '../types/person.type';
import { startTrainingSeriesTour, startTrainingSeriesTourOnFirstVisit } from '../helpers/trainingTour';

function timeToMins(time: string): number {
  const [h, m] = (time || '00:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

const EVENT_TYPE_OPTIONS = [
  { label: 'Training', value: TrainingEventType.TRAINING },
  { label: 'Spiel', value: TrainingEventType.MATCH },
  { label: 'Veranstaltung', value: TrainingEventType.EVENT },
];

// value = JS Date#getUTCDay() (0=Sun..6=Sat), matching the server's
// createSeries weekday convention. Displayed Monday-first for readability.
const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Mo' },
  { value: 2, label: 'Di' },
  { value: 3, label: 'Mi' },
  { value: 4, label: 'Do' },
  { value: 5, label: 'Fr' },
  { value: 6, label: 'Sa' },
  { value: 0, label: 'So' },
];

const eventClient = new HttpClient<TrainingEvent>('/training-event');

const TrainingSeriesCreate: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [tags, setTags] = useState<Tag[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [startTimeStr, setStartTimeStr] = useState('17:00');
  const [duration, setDuration] = useState<number | string>(90);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [assignedPersonId, setAssignedPersonId] = useState<string | null>(null);
  const [coTrainerIds, setCoTrainerIds] = useState<string[]>([]);
  const [eventType, setEventType] = useState<string>(TrainingEventType.TRAINING);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const tagClient = new HttpClient<Tag>('/tag');
    const personClient = new HttpClient<Person>('/person');

    Promise.all([
      tagClient.getAll(),
      personClient.getAll({ slim: 'true' }),
    ]).then(([allTags, allPersons]) => {
      setTags(allTags as Tag[]);
      setPersons(allPersons as Person[]);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading) startTrainingSeriesTourOnFirstVisit();
  }, [loading]);

  const toggleWeekday = (value: number) => {
    setWeekdays((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const tagOptions = tags.map((t) => ({ value: t.id.toString(), label: t.name }));
  const personOptions = persons.map((p) => ({ value: p.id.toString(), label: `${p.firstName} ${p.lastName}` }));

  const canSubmit = !!startDate && !!endDate && weekdays.length > 0 && tagIds.length > 0 && !!assignedPersonId;

  const handleSubmit = async () => {
    if (!canSubmit) {
      notifications.show({
        color: 'red',
        message: 'Bitte Start-/Enddatum, mindestens einen Wochentag, mindestens einen Tag und einen Trainer angeben.',
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        startDate,
        endDate,
        weekdays,
        startTime: timeToMins(startTimeStr),
        duration: Number(duration),
        tagIds: tagIds.map(Number),
        assignedPersonId: Number(assignedPersonId),
        coTrainerIds: coTrainerIds.map(Number),
        eventType,
        notes: notes || undefined,
      };
      const created = await eventClient.postAction('series', payload) as unknown as TrainingEvent[];
      notifications.show({ color: 'green', message: `${created.length} Trainings erstellt.` });
      navigate('/training/events');
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Erstellen der Serie.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Text p="md">Laden...</Text>;

  return (
    <Stack p="md" gap="md" maw={640}>
      <Group justify="space-between" align="center">
        <Title order={2}>Trainingsserie erstellen</Title>
        <Button variant="subtle" leftSection={<IconHelp size={16} />} onClick={startTrainingSeriesTour}>
          Anleitung
        </Button>
      </Group>

      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Stack gap="sm">
          <Group grow data-tour="series-dates">
            <DateInput
              label="Von"
              value={startDate}
              onChange={setStartDate}
              valueFormat="DD.MM.YYYY"
              required
            />
            <DateInput
              label="Bis"
              value={endDate}
              onChange={setEndDate}
              valueFormat="DD.MM.YYYY"
              required
            />
          </Group>

          <div data-tour="series-weekdays">
            <Text size="sm" fw={500} mb={4}>Wochentage</Text>
            <Group gap="xs">
              {WEEKDAY_OPTIONS.map((d) => (
                <Badge
                  key={d.value}
                  variant={weekdays.includes(d.value) ? 'filled' : 'outline'}
                  color="indigo"
                  size="lg"
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggleWeekday(d.value)}
                >
                  {d.label}
                </Badge>
              ))}
            </Group>
          </div>

          <Group grow data-tour="series-time">
            <TimeInput
              label="Startzeit"
              value={startTimeStr}
              onChange={(e) => setStartTimeStr(e.currentTarget.value)}
            />
            <NumberInput
              label="Dauer (Minuten)"
              value={duration}
              onChange={setDuration}
              min={1}
              required
            />
          </Group>

          <Select
            label="Typ"
            data={EVENT_TYPE_OPTIONS}
            value={eventType}
            onChange={(v) => setEventType(v ?? TrainingEventType.TRAINING)}
          />

          <div data-tour="series-tags">
            <MultiSelect
              label="Tags"
              data={tagOptions}
              value={tagIds}
              onChange={setTagIds}
              searchable
              required
              description="Personen mit einem dieser Tags werden automatisch als Teilnehmer eingetragen (Personen mit dem Tag 'Inaktiv' ausgenommen)."
            />
          </div>

          <Stack gap="sm" data-tour="series-trainer">
            <Select
              label="Trainer / Verantwortlich"
              data={personOptions}
              value={assignedPersonId}
              onChange={(v) => {
                setAssignedPersonId(v);
                setCoTrainerIds((prev) => prev.filter((pid) => pid !== v));
              }}
              searchable
              required
            />

            <MultiSelect
              label="Weitere Trainer"
              data={personOptions.filter((p) => p.value !== assignedPersonId)}
              value={coTrainerIds}
              onChange={setCoTrainerIds}
              searchable
            />
          </Stack>

          <Textarea
            label="Notizen"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
          />
        </Stack>
      </Paper>

      <Group data-tour="series-submit">
        <Button onClick={handleSubmit} loading={saving} disabled={!canSubmit}>
          Serie erstellen
        </Button>
        <Button variant="subtle" onClick={() => navigate('/training/events')}>
          Abbrechen
        </Button>
      </Group>
    </Stack>
  );
};

export default TrainingSeriesCreate;
